import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, PlayCircle, ChevronLeft, ChevronRight, ShieldCheck, Clock, Lock, Headphones } from "lucide-react";
import { Button } from "../../components/ui/button";
import EgHeaderNav from "../../components/EgHeaderNav";
import EgVideoModal from "../../components/EgVideoModal";
import SampleLeadCapture from "../../components/SampleLeadCapture";
import Logo from "../../components/Logo";
import { trackVisit, createOrder, trackCtaClick } from "../../lib/api";
import { trackClickButton, trackAddToCart, trackInitiateCheckout, trackViewContent } from "../../lib/tiktok";
import { egPrice, EG_MAIN_EGP } from "../../lib/formatEg";
import { EG_PREVIEW_PAGES } from "../../lib/egConstants";
import { toast } from "sonner";

const main = egPrice(EG_MAIN_EGP, { withAnchor: true });

export default function EgSample() {
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackVisit("eg-sample");
    trackViewContent("pdf");
    try { sessionStorage.setItem("bb_funnel", "eg"); } catch (_) { /* ignore */ }
  }, []);

  const startCheckout = async (source) => {
    if (loading) return;
    setLoading(true);
    trackClickButton(source);
    trackCtaClick(`sample-buy-${source}`);
    trackAddToCart("pdf_audio_eg");
    trackInitiateCheckout("pdf_audio_eg");
    try {
      const r = await createOrder("pdf_audio_eg");
      navigate(`/checkout/${r.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء عملية الدفع، حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  const next = () => setActive((i) => (i + 1) % EG_PREVIEW_PAGES.length);
  const prev = () => setActive((i) => (i - 1 + EG_PREVIEW_PAGES.length) % EG_PREVIEW_PAGES.length);

  return (
    <div className="min-h-screen bg-[#040914] text-white" dir="rtl" data-testid="eg-sample-root">
      <EgHeaderNav variant="static" />

      <section className="py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-9 sm:mb-12">
            <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3">عينة من الكتاب</div>
            <h1 className="font-display text-3xl sm:text-5xl font-black leading-tight">
              شاهد عينة من داخل <span className="text-[#C5A059]">الكتاب الأزرق</span>
            </h1>
            <p className="mt-5 text-sm sm:text-lg text-[#A1B0C6] max-w-2xl mx-auto leading-relaxed">
              3 صفحات حقيقية من الكتاب + فيديو قصير بيدخلك في أول سر — قبل ما تشتري.
            </p>
          </div>

          {/* Video CTA card */}
          <div
            className="mb-12 sm:mb-16 bg-gradient-to-br from-[#C5A059]/18 via-[#C5A059]/8 to-transparent border-2 border-[#C5A059]/50 rounded-sm p-5 sm:p-8 text-center shadow-[0_0_60px_rgba(197,160,89,0.18)]"
            data-testid="sample-video-card"
          >
            <div className="flex items-center justify-center gap-2 text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3 font-bold">
              <PlayCircle className="w-4 h-4" />
              <span>فيديو حصري · مجاناً</span>
            </div>
            <h2 className="font-display text-xl sm:text-3xl font-black leading-snug">
              اسمع أول سر من <span className="text-[#C5A059]">الكتاب الأزرق</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/85">شغّل الفيديو دلوقتي مجاناً</p>
            <Button
              onClick={() => { trackCtaClick("sample-video-open"); trackClickButton("sample-video-open"); setVideoOpen(true); }}
              data-testid="sample-video-open-btn"
              className="mt-5 w-full max-w-md bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-base sm:text-lg py-6 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.4)]"
            >
              <PlayCircle className="w-5 h-5 ml-2" />
              تشغيل الفيديو
            </Button>
          </div>

          {/* Preview pages carousel */}
          <div className="mb-12 sm:mb-16" data-testid="sample-pages-block">
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex items-center justify-center gap-2 text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3">
                <BookOpen className="w-4 h-4" />
                <span>3 صفحات من داخل الكتاب</span>
              </div>
              <h2 className="font-display text-xl sm:text-3xl font-black">العينة الكاملة</h2>
            </div>

            <div className="relative mx-auto" style={{ maxWidth: "460px" }}>
              <div className="absolute inset-0 bg-[radial-gradient(closest-side,rgba(197,160,89,0.18),transparent_70%)] blur-2xl -z-10" />
              <div className="relative w-full rounded-[3px] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_0_1px_rgba(197,160,89,0.25)] bg-[#fdfbf6]">
                <img
                  key={active}
                  src={EG_PREVIEW_PAGES[active]}
                  alt={`صفحة ${active + 1} من الكتاب`}
                  className="w-full h-auto block select-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid={`sample-page-${active}`}
                />
              </div>
              <button
                onClick={prev}
                aria-label="السابق"
                data-testid="sample-prev"
                className="absolute right-1 sm:-right-9 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0B1D3A]/90 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] transition flex items-center justify-center shadow-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="التالي"
                data-testid="sample-next"
                className="absolute left-1 sm:-left-9 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0B1D3A]/90 border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] transition flex items-center justify-center shadow-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-center gap-1.5 mt-5">
              {EG_PREVIEW_PAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`اذهب إلى الصفحة ${i + 1}`}
                  data-testid={`sample-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active ? "w-6 bg-[#C5A059]" : "w-1.5 bg-white/25 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
            <div className="text-center text-[11px] text-[#A1B0C6] mt-2">
              {active + 1} / {EG_PREVIEW_PAGES.length}
            </div>
          </div>

          {/* Warm-lead capture — appears before the final Buy CTA */}
          <div className="mb-8 sm:mb-10">
            <SampleLeadCapture locale="eg" />
          </div>

          {/* Final Buy CTA */}
          <div
            className="bg-[#0B1D3A]/80 border border-[#C5A059]/30 rounded-sm p-6 sm:p-9 text-center shadow-[0_0_40px_rgba(197,160,89,0.12)]"
            data-testid="sample-buy-block"
          >
            <h2 className="font-display text-xl sm:text-3xl font-black leading-snug">
              عاجبك اللي شفته؟<br className="sm:hidden"/> احصل على <span className="text-[#C5A059]">الكتاب الكامل</span> دلوقتي
            </h2>
            <div className="mt-3 text-base sm:text-lg text-white/90 flex items-baseline justify-center gap-2.5 flex-wrap" data-testid="sample-price-row">
              {main.anchor && (
                <span className="text-lg sm:text-2xl text-[#A1B0C6] line-through decoration-[#A1B0C6]/70 decoration-2" data-testid="sample-price-anchor">
                  بدل {main.anchor}
                </span>
              )}
              <span className="font-black text-[#C5A059] text-3xl sm:text-5xl">{main.primaryNumber}</span>
              <span className="text-base sm:text-xl text-[#C5A059]">جنيه مصري</span>
            </div>
            {main.anchorReason && (
              <div className="mt-2 inline-block bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059] text-[11px] sm:text-xs tracking-[0.15em] font-bold px-3 py-1 rounded-full" data-testid="sample-price-anchor-reason">
                {main.anchorReason}
              </div>
            )}
            <div className="mt-2 text-xs sm:text-sm text-[#A1B0C6]">يشمل النسخة الصوتية مجاناً</div>
            <Button
              data-testid="sample-buy-btn"
              onClick={() => startCheckout("cta")}
              disabled={loading}
              className="mt-6 w-full max-w-md bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-black text-base sm:text-lg py-7 rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse"
            >
              احصل على الكتاب الآن · {main.primary}
            </Button>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] sm:text-xs text-[#A1B0C6]">
              <span className="flex items-center gap-1"><Headphones className="w-3.5 h-3.5 text-[#C5A059]" /> نسخة صوتية</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C5A059]" /> تسليم فوري</span>
              <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</span>
              <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]" /> ضمان 7 أيام</span>
            </div>
          </div>
        </div>
      </section>

      <EgVideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        onBuy={() => { setVideoOpen(false); startCheckout("video-modal"); }}
        ctaLabel="sample-video-modal-buy"
      />

      <footer className="border-t border-[#C5A059]/15 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1B0C6]">
          <Logo className="h-9 w-9" />
          <div>© {new Date().getFullYear()} الكتاب الأزرق</div>
          <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</div>
        </div>
      </footer>
    </div>
  );
}
